// Pure projections for the Project drawer's shared fixed header. Inputs are
// the one loaded ProjectFullPage record + milestone collection; nothing is
// fetched here and nothing is invented: missing values become honest gaps.
import { PROJECT_STATUS } from "../../../app/tokens.js";
import { getProjectPhaseSteps } from "../projectFilters.js";
import { formatRecordAmount } from "../projectRecord.js";
import { typePillMeta } from "../briefing/landingModel.js";
import { interpretConnections } from "../connections/projectConnectionsModel.js";
import { fmtDate } from "../../../utils/format.js";
import { budgetHealth, milestoneSignal } from "../../../lib/projectDisplaySignals.js";
import { budgetHealthColor, budgetHealthText } from "../ProjectDisplaySignals.jsx";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Mar '25" from a real date value; null when absent/invalid. */
export function monthYear(value) {
  if (value == null || value === "") return null;
  const s = value instanceof Date ? value.toISOString() : String(value);
  const m = /^(\d{4})-(\d{2})/.exec(s);
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return `${MONTHS[month - 1]} '${m[1].slice(2)}`;
}

export function projectCode(project) {
  const fromConnections = interpretConnections(project?.connections).code;
  const raw = fromConnections || project?.projectCode || project?.project_code;
  return raw && String(raw).trim() ? String(raw).trim() : null;
}

export function officeLabel(project) {
  if (!project) return null;
  return [project.locationName, project.locationAlias].filter(Boolean).join(" · ") || project.locationId || null;
}

/** Meta segments: code · type · office (unavailable segments omitted). */
export function headerMeta(project) {
  if (!project) return { code: null, type: null, office: null, text: "" };
  const code = projectCode(project);
  const type = project.projectType ? typePillMeta(project.projectType)?.label || null : null;
  const office = officeLabel(project);
  return { code, type, office, text: [code, type, office].filter(Boolean).join(" · ") };
}

export function phaseCell(project) {
  const steps = getProjectPhaseSteps(project?.phase);
  const n = steps.length;
  const started = monthYear(project?.startDate);
  const startedSub = started ? ` · started ${started}` : "";
  if (steps.every((s) => s.state === "done")) return { value: "Complete", sub: `${n} of ${n}${startedSub}` };
  const i = steps.findIndex((s) => s.state === "current");
  if (i < 0) return { value: "Not mapped", sub: project?.phase ? `recorded: ${project.phase}` : "phase not recorded", gap: true };
  return { value: steps[i].label, sub: `${i + 1} of ${n}${startedSub}` };
}

export function budgetCell(project, canSeeBudget) {
  if (!canSeeBudget) return { value: "Restricted", sub: "financial access required", restricted: true };
  const value = formatRecordAmount(project?.approvedBudget, project?.currency);
  const health = budgetHealth(project?.financialsTracked === false ? null : project?.approvedBudget, project?.forecastAtCompletion);
  return { value: value === "Unknown" ? "Data gap" : value, sub: health.overage != null ? `Over by ${formatRecordAmount(health.overage, project?.currency)}` : budgetHealthText(health), color: budgetHealthColor(health), gap: value === "Unknown" };
}

export function fdobCell(project) {
  const d = project?.fdobDate ? fmtDate(project.fdobDate) : null;
  const sub = project?.stage === "completed" ? "delivered" : "target";
  return d && d !== "—" ? { value: d, sub } : { value: "Data gap", sub, gap: true };
}

export function statusCell(project, milestones) {
  if (project?.stage === "completed") return { value: "Completed", sub: `on ${fmtDate(project.completedOn)}`, color: null };
  if (project?.stage === "cancelled") return { value: "Cancelled", sub: "", color: null };
  const st = !project ? null : PROJECT_STATUS[project.statusOverall] || null;
  let sub;
  if (!milestones || milestones.status === "loading" || milestones.status === "idle") sub = "checking milestones";
  else if (milestones.status !== "ok") sub = "milestones unavailable";
  else {
    const n = milestones.items.filter((m) => m?.status === "at_risk" && !m.deletedAt).length;
    const overdue = milestones.items.filter(m => milestoneSignal(m, project?.projectTimeZone).overdue).length;
    sub = n ? `${n} milestone${n === 1 ? "" : "s"} at risk` : "no open risks";
    if (overdue) sub += ` · ${overdue} overdue`;
    else if (milestones.items.some(m => milestoneSignal(m, project?.projectTimeZone).reason?.startsWith("Project time zone"))) sub += " · overdue unknown (time zone unavailable)";
  }
  return { value: st?.label || "Not set", color: st?.color || null, sub, gap: !st };
}
